import axiosInstance from '../api/AxiosInstance';

export interface TipoTramite {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  activo: boolean;
  requiereUbicacion: boolean;
  requiereDireccion: boolean;
  requisitos?: Requisito[];
}

export interface Requisito {
  id: number;
  idTipoTramite: number;
  nombreRequisito: string;
  descripcion?: string;
  obligatorio: boolean;
  orden: number;
}

export interface Tramite {
  id: number;
  folio: string;
  idTipoTramite: number;
  fechaRegistro: string;
  fechaSolicitud: string;
  fechaSolicitudEsRegistro: boolean;
  nombreSolicitante: string;
  telefono: string;
  correo: string;
  nomenclatura?: string;
  direccion?: string;
  observaciones?: string;
  estatus: string;
  origen: string;
  usuarioRegistro?: string;
  tipoTramite?: {
    id: number;
    nombre: string;
    codigo: string;
  };
  documentos?: Documento[];
  ubicaciones?: Ubicacion[];
  historial?: HistorialCambio[];
  _count?: {
    documentos: number;
  };
}

export interface Documento {
  id: number;
  idTramite: number;
  idRequisito?: number;
  nombreArchivo: string;
  rutaArchivo: string;
  tipoArchivo: string;
  descripcion?: string;
  esSolicitud: boolean;
  fechaSubida: string;
  usuarioSubida?: string;
  requisito?: Requisito;
}

export interface Ubicacion {
  id: number;
  idTramite: number;
  latitud: number;
  longitud: number;
  urlMaps?: string;
  descripcion?: string;
}

export interface HistorialCambio {
  id: number;
  idTramite: number;
  tipoAccion: string;
  estatusAnterior?: string;
  estatusNuevo?: string;
  comentario?: string;
  observaciones?: string;
  documentoNombre?: string;
  usuario?: string;
  fechaCambio: string;
}

export interface Estadisticas {
  total: number;
  porEstatus: Array<{ estatus: string; _count: { estatus: number } }>;
  porOrigen: Array<{ origen: string; _count: { origen: number } }>;
  porTipo: Array<{ tipo: string; cantidad: number }>;
}

export interface FiltrosTramites {
  folio?: string;
  fechaInicio?: string;
  fechaFin?: string;
  idTipoTramite?: number;
  estatus?: string;
  origen?: string;
  buscar?: string;
}

class TramitesObrasService {
  // ========== TIPOS DE TRÁMITES ==========
  async obtenerTiposTramites(): Promise<TipoTramite[]> {
    const response = await axiosInstance.get('/tramites-obras/tipos');
    return response.data;
  }

  async obtenerTipoTramite(id: number): Promise<TipoTramite> {
    const response = await axiosInstance.get(`/tramites-obras/tipos/${id}`);
    return response.data;
  }

  // ========== TRÁMITES ==========
  async obtenerTramites(filtros?: FiltrosTramites): Promise<Tramite[]> {
    const response = await axiosInstance.get('/tramites-obras', { params: filtros });
    return response.data;
  }

  async obtenerTramite(id: number): Promise<Tramite> {
    const response = await axiosInstance.get(`/tramites-obras/${id}`);
    return response.data;
  }

  async crearTramite(data: Partial<Tramite>): Promise<Tramite> {
    const response = await axiosInstance.post('/tramites-obras', data);
    return response.data;
  }

  async actualizarTramite(id: number, data: Partial<Tramite>): Promise<Tramite> {
    const response = await axiosInstance.put(`/tramites-obras/${id}`, data);
    return response.data;
  }

  async actualizarEstatus(
    id: number,
    estatus: string,
    comentario?: string,
    usuario?: string
  ): Promise<Tramite> {
    const response = await axiosInstance.put(`/tramites-obras/${id}/estatus`, {
      estatus,
      comentario,
      usuario,
    });
    return response.data;
  }

  async obtenerHistorial(id: number): Promise<HistorialCambio[]> {
    const response = await axiosInstance.get(`/tramites-obras/${id}/historial`);
    return response.data;
  }

  // ========== DOCUMENTOS ==========
  async subirDocumento(
    idTramite: number,
    archivo: File,
    data: {
      idRequisito?: number;
      descripcion?: string;
      esSolicitud?: boolean;
      usuario?: string;
    }
  ): Promise<Documento> {
    const formData = new FormData();
    formData.append('archivo', archivo);
    if (data.idRequisito) formData.append('idRequisito', String(data.idRequisito));
    if (data.descripcion) formData.append('descripcion', data.descripcion);
    if (data.esSolicitud !== undefined) formData.append('esSolicitud', String(data.esSolicitud));
    if (data.usuario) formData.append('usuario', data.usuario);

    const response = await axiosInstance.post(
      `/tramites-obras/${idTramite}/documentos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async eliminarDocumento(idTramite: number, documentoId: number, usuario?: string): Promise<void> {
    await axiosInstance.delete(`/tramites-obras/${idTramite}/documentos/${documentoId}`, {
      data: { usuario },
    });
  }

  getUrlDescargaDocumento(idTramite: number, documentoId: number): string {
    return `${axiosInstance.defaults.baseURL}/tramites-obras/${idTramite}/documentos/${documentoId}/descargar`;
  }

  // ========== COMENTARIOS ==========
  async agregarComentario(idTramite: number, comentario: string, usuario?: string): Promise<void> {
    await axiosInstance.post(`/tramites-obras/${idTramite}/comentarios`, {
      comentario,
      usuario,
    });
  }

  // ========== ESTADÍSTICAS ==========
  async obtenerEstadisticas(): Promise<Estadisticas> {
    const response = await axiosInstance.get('/tramites-obras/estadisticas');
    return response.data;
  }

  // ========== REPORTES PDF ==========
  getUrlReportePDF(filtros?: FiltrosTramites): string {
    const params = new URLSearchParams();
    if (filtros) {
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const queryString = params.toString();
    return `${axiosInstance.defaults.baseURL}/tramites-obras/reporte/pdf${queryString ? '?' + queryString : ''}`;
  }

  getUrlDetallePDF(id: number): string {
    return `${axiosInstance.defaults.baseURL}/tramites-obras/${id}/pdf`;
  }
}

export default new TramitesObrasService();
